/**
 * Wire schemas for Phase 4: supplier catalogues, the marketplace and
 * procurement from requisition to supplier invoice.
 *
 * Synced (offline) on a site device: `requisition` (drafted, edited and
 * submitted offline), `purchase_order` (read-only, so the site can receive
 * against it without signal) and `delivery` (received offline; evidence, never
 * edited). Everything else — RFQs, quotations, comparison, awards, order and
 * invoice decisions, and the whole supplier portal — is online only.
 *
 * The API renders decimals (quantities) as strings. Requisition lines keep
 * them as the server's exact strings: an offline `{changes, base}` update of
 * `lines` is merged by comparing the whole list, so `base` must be the
 * server's own JSON (see `normalizeRequisitionLines`). Elsewhere quantities
 * are coerced to numbers for display. Money stays `{amount_minor, currency}`.
 */
import { z } from 'zod';

import { moneySchema } from './workforce-types';

const id = z.string();
const nullableId = z.string().nullable().catch(null).default(null);
const date = z.string().nullable().catch(null).default(null);
const text = z.string().catch('').default('');
const decimal = z.coerce.number().catch(0).default(0);
const integer = z.number().int().catch(0).default(0);
const version = z.number().int().nullable().catch(null).default(null);
const nullableMoney = moneySchema.nullable().catch(null).default(null);
/** An exact decimal as the server sent it ("120.000"); numbers become strings. */
const decimalString = z
  .union([z.string(), z.number()])
  .transform((value) => String(value))
  .catch('0')
  .default('0');

export type Money = z.infer<typeof moneySchema>;

// ---------------------------------------------------------------------------
// Requisitions (synced; offline create / update / submit)
// ---------------------------------------------------------------------------

export const REQUISITION_STATUSES = [
  'draft',
  'submitted',
  'approved',
  'rejected',
  'ordered',
] as const;

/** Module 12: the catalogue price a shop checkout fixed for a line (server-made, never edited). */
export const shopSnapshotSchema = z.object({
  supplier_org_id: z.string(),
  supplier_product_id: z.string(),
  unit_price_minor: z.number(),
  currency: z.string(),
  tax_inclusive: z.boolean().catch(true).default(true),
  vat_category: z.string().catch('standard').default('standard'),
  delivery_date: z.string().nullable().catch(null).default(null),
  delivery_site: z.string().catch('').default(''),
});
export type ShopSnapshot = z.infer<typeof shopSnapshotSchema>;

export const requisitionLineSchema = z.object({
  id,
  description: text,
  quantity: decimalString,
  unit: text,
  specification: text,
  boq_lineage: nullableId,
  product_id: nullableId,
  /** Present only on lines checked out of a supplier's shop (module 12). */
  shop: shopSnapshotSchema.optional().catch(undefined),
});
export type RequisitionLine = z.infer<typeof requisitionLineSchema>;

export const requisitionSchema = z.object({
  id,
  project_id: id,
  /** "R001" — empty until the server has numbered it. */
  reference: text,
  number: integer,
  title: text,
  needed_by: date,
  stage_id: nullableId,
  notes: text,
  lines: z.array(requisitionLineSchema).catch([]).default([]),
  status: z.string().default('draft'),
  requested_by_id: nullableId,
  decided_at: date,
  decision_note: text,
  /** Module 12: checked out of this supplier's shop; approving it places the order. */
  supplier_org_id: nullableId,
  supplier: z.string().nullable().catch(null).default(null),
  version,
});
export type Requisition = z.infer<typeof requisitionSchema>;

/** Fields an offline update may change (server `UpdateRequisition.EDITABLE`). */
export const REQUISITION_EDITABLE = ['title', 'needed_by', 'stage_id', 'notes', 'lines'] as const;

export type RequisitionAction = 'approve' | 'reject';

// ---------------------------------------------------------------------------
// RFQs, quotations, comparison (online)
// ---------------------------------------------------------------------------

export const RFQ_STATUSES = ['draft', 'open', 'closed', 'awarded', 'cancelled'] as const;
export type RfqAction = 'send' | 'close' | 'cancel';

export const rfqLineSchema = z.object({
  id,
  description: text,
  specification: text,
  quantity: decimal,
  unit: text,
  product_id: nullableId,
  boq_lineage: nullableId,
});
export type RfqLine = z.infer<typeof rfqLineSchema>;

export const invitationSchema = z.object({
  supplier_org_id: id,
  supplier: text,
  /** invited | viewed | declined | quoted */
  status: z.string().default('invited'),
  decline_reason: text,
});
export type Invitation = z.infer<typeof invitationSchema>;

export const quotationLineSchema = z.object({
  rfq_line_id: id,
  offered_quantity: decimal,
  unit: text,
  unit_price: moneySchema,
  line_total: moneySchema,
  brand: text,
  alternative: text,
  availability: z.string().default('in_stock'),
});
export type QuotationLine = z.infer<typeof quotationLineSchema>;

export const quotationSchema = z.object({
  id,
  rfq_id: id,
  supplier_org_id: id,
  supplier: text,
  /** draft | submitted | withdrawn | accepted | rejected */
  status: z.string().default('draft'),
  valid_until: date,
  lead_time_days: integer,
  payment_terms: text,
  subtotal: moneySchema,
  delivery_fee: moneySchema,
  discount: moneySchema,
  tax: moneySchema,
  total: moneySchema,
  notes: text,
  submitted_at: date,
  lines: z.array(quotationLineSchema).catch([]).default([]),
});
export type Quotation = z.infer<typeof quotationSchema>;

const rfqBase = {
  id,
  project_id: id,
  reference: text,
  title: text,
  delivery_site: text,
  delivery_region: text,
  required_by: date,
  closes_at: date,
  notes: text,
  status: z.string().default('draft'),
  /** The buyer project's currency: quotations are priced in it. */
  currency: z.string().nullable().catch(null).default(null),
  sent_at: date,
  lines: z.array(rfqLineSchema).catch([]).default([]),
  version,
};

/** An RFQ as the buyer sees it (with whom it invited). */
export const rfqSchema = z.object({
  ...rfqBase,
  requisition_id: nullableId,
  invitations: z.array(invitationSchema).catch([]).default([]),
});
export type Rfq = z.infer<typeof rfqSchema>;

/** An RFQ as an invited supplier sees it: who asks, never who else was asked. */
export const supplierRfqSchema = z.object({
  ...rfqBase,
  /** The buyer's project name. */
  buyer: text,
  invitation_status: z.string().default('invited'),
  quotation: quotationSchema.nullable().catch(null).default(null),
});
export type SupplierRfq = z.infer<typeof supplierRfqSchema>;

export const comparisonOfferSchema = z.object({
  quotation_id: id,
  supplier: text,
  unit_price: moneySchema,
  line_total: moneySchema,
  offered_quantity: decimal,
  unit: text,
  brand: text,
  alternative: text,
  availability: text,
  /** Same unit, full quantity, not an alternative — only these may be ranked. */
  comparable: z.boolean().catch(false).default(false),
  lowest: z.boolean().catch(false).default(false),
  vs_boq_percent: z.number().nullable().optional(),
});
export type ComparisonOffer = z.infer<typeof comparisonOfferSchema>;

export const comparisonSchema = z.object({
  lines: z
    .array(
      z.object({
        rfq_line_id: id,
        description: text,
        quantity: decimal,
        unit: text,
        boq_rate: nullableMoney,
        offers: z.array(comparisonOfferSchema).catch([]).default([]),
      }),
    )
    .catch([])
    .default([]),
  totals: z
    .array(
      z.object({
        quotation_id: id,
        supplier: text,
        subtotal: moneySchema,
        delivery_fee: moneySchema,
        discount: moneySchema,
        tax: moneySchema,
        landed_total: moneySchema,
        lead_time_days: integer,
        valid_until: date,
        payment_terms: text,
        lines_quoted: integer,
      }),
    )
    .catch([])
    .default([]),
  line_count: integer,
});
export type Comparison = z.infer<typeof comparisonSchema>;

// ---------------------------------------------------------------------------
// Purchase orders (synced read-only), deliveries (synced; offline create),
// supplier invoices (online)
// ---------------------------------------------------------------------------

export const PO_STATUSES = [
  'draft',
  'pending_approval',
  'approved',
  'sent',
  'acknowledged',
  'partially_delivered',
  'delivered',
  'closed',
  'cancelled',
  'declined',
] as const;
export type PoAction = 'submit' | 'approve' | 'send' | 'cancel' | 'close';
/** Goods may be received against an order in these states (server `RecordDelivery`). */
export const RECEIVABLE_PO_STATUSES: readonly string[] = [
  'approved',
  'sent',
  'acknowledged',
  'partially_delivered',
];
/** Approved orders count as committed cost; invoices may be recorded against them. */
export const COMMITTED_PO_STATUSES: readonly string[] = [
  'approved',
  'sent',
  'acknowledged',
  'partially_delivered',
  'delivered',
  'closed',
];

export const poLineSchema = z.object({
  id,
  description: text,
  quantity: decimal,
  unit: text,
  unit_price: moneySchema,
  line_total: moneySchema,
  boq_lineage: nullableId,
  /** Accepted on site so far (deliveries plus corrections). */
  accepted_quantity: decimal,
});
export type PoLine = z.infer<typeof poLineSchema>;

export const purchaseOrderSchema = z.object({
  id,
  project_id: id,
  reference: text,
  supplier_org_id: id,
  supplier: text,
  quotation_id: nullableId,
  status: z.string().default('draft'),
  delivery_site: text,
  required_by: date,
  payment_terms: text,
  subtotal: moneySchema,
  delivery_fee: moneySchema,
  discount: moneySchema,
  tax: moneySchema,
  total: moneySchema,
  notes: text,
  created_by_id: nullableId,
  approved_at: date,
  sent_at: date,
  acknowledged_at: date,
  // Module 12
  /** direct | rfq | shop */
  origin: z.string().catch('direct').default('direct'),
  requisition_id: nullableId,
  /** The buying company (what a supplier calls its customer). */
  buyer_company: z.string().nullable().catch(null).default(null),
  /** The project's name (as the supplier sees who is buying). */
  buyer: text,
  confirmed_delivery_date: date,
  fulfilment_branch: z.string().nullable().catch(null).default(null),
  declined_at: date,
  decline_reason: text,
  /** A delivery note is on the road (dispatched, not yet received). */
  in_transit: z.boolean().catch(false).default(false),
  lines: z.array(poLineSchema).catch([]).default([]),
  version,
});
export type PurchaseOrder = z.infer<typeof purchaseOrderSchema>;

export const deliveryLineSchema = z.object({
  po_line_id: id,
  delivered: decimal,
  accepted: decimal,
  rejected: decimal,
  damaged: decimal,
  note: text,
});
export type DeliveryLine = z.infer<typeof deliveryLineSchema>;

export const deliverySchema = z.object({
  id,
  project_id: id,
  purchase_order_id: id,
  delivery_note_number: text,
  received_at: date,
  received_by_id: nullableId,
  vehicle: text,
  driver: text,
  notes: text,
  lines: z.array(deliveryLineSchema).catch([]).default([]),
  signature_asset_id: nullableId,
  /** The supplier's delivery note this load came with (module 12). */
  dispatch_id: nullableId,
  version,
});
export type Delivery = z.infer<typeof deliverySchema>;

export const INVOICE_STATUSES = [
  'received',
  'matched',
  'disputed',
  'approved',
  'paid',
  'rejected',
] as const;
export type InvoiceAction = 'approve' | 'reject' | 'mark_paid';

/** One three-way-match finding (`kind`: price | quantity | arithmetic | possible_duplicate). */
export const discrepancySchema = z
  .object({
    kind: z.string().default(''),
    po_line_id: z.string().optional(),
  })
  .catchall(z.unknown());
export type Discrepancy = z.infer<typeof discrepancySchema>;

export const supplierInvoiceSchema = z.object({
  id,
  project_id: id,
  purchase_order_id: id,
  supplier_org_id: id,
  invoice_number: text,
  invoice_date: date,
  due_date: date,
  subtotal: moneySchema,
  tax: moneySchema,
  total: moneySchema,
  status: z.string().default('received'),
  /** supplier_portal | photo_capture */
  source: text,
  discrepancies: z.array(discrepancySchema).catch([]).default([]),
  possible_duplicate_of: nullableId,
  document_asset_id: nullableId,
  /** Whoever recorded it cannot approve it; their approve button hides. */
  recorded_by_id: nullableId,
  approved_at: date,
  paid_at: date,
  payment_reference: text,
  // Module 12: accepted credit notes, what the buyer paid, the supplier's acknowledgement.
  credited: nullableMoney,
  amount_paid: nullableMoney,
  payment_acknowledged_at: date,
  lines: z
    .array(
      z.object({
        po_line_id: id,
        quantity: decimal,
        unit_price: moneySchema,
        line_total: moneySchema,
      }),
    )
    .catch([])
    .default([]),
});
export type SupplierInvoice = z.infer<typeof supplierInvoiceSchema>;

/** A purchase order with what happened against it (detail reads). */
export const purchaseOrderDetailSchema = purchaseOrderSchema.extend({
  deliveries: z.array(deliverySchema).catch([]).default([]),
  invoices: z.array(supplierInvoiceSchema).catch([]).default([]),
});
export type PurchaseOrderDetail = z.infer<typeof purchaseOrderDetailSchema>;

// ---------------------------------------------------------------------------
// Catalogue & marketplace
// ---------------------------------------------------------------------------

export const categorySchema = z.object({
  key: id,
  name: text,
  standard_unit: text,
  /** The attributes that decide whether two products are the same thing. */
  attributes: z.array(z.string()).catch([]).default([]),
});
export type Category = z.infer<typeof categorySchema>;

export const STOCK_STATUSES = [
  'in_stock',
  'low',
  'out_of_stock',
  'made_to_order',
  'on_order',
] as const;
export const VAT_CATEGORIES = ['standard', 'zero_rated', 'exempt'] as const;

/** A volume price: from `min_quantity` packs up (module 12). */
export const priceTierSchema = z.object({ min_quantity: decimal, price: moneySchema });
export type PriceTier = z.infer<typeof priceTierSchema>;

const attributes = z.record(z.string(), z.unknown()).catch({}).default({});

export const offerSchema = z.object({
  supplier_product_id: id,
  product_id: nullableId,
  supplier: z.object({
    id,
    name: text,
    verification_status: z.string().default('unverified'),
  }),
  original_name: text,
  brand: text,
  attributes,
  pack_description: text,
  standard_units_per_pack: decimal,
  price: moneySchema,
  tax_inclusive: z.boolean().catch(true).default(true),
  price_per_standard_unit: moneySchema,
  standard_unit: text,
  price_updated_at: date,
  /** Not refreshed within the server's freshness window. */
  stale: z.boolean().catch(false).default(false),
  stock_status: z.string().default('in_stock'),
  lead_time_days: integer,
  delivers: z.boolean().catch(false).default(false),
  // Module 12
  price_valid_until: date,
  tiers: z.array(priceTierSchema).catch([]).default([]),
  vat_category: z.string().catch('standard').default('standard'),
  variant_group: nullableId,
  variant_label: text,
  min_order_packs: decimal,
  image_urls: z.array(z.string()).catch([]).default([]),
});
export type Offer = z.infer<typeof offerSchema>;

export const searchResultSchema = z.object({
  comparable: z
    .array(
      z.object({
        product_id: id,
        name: text,
        attributes,
        standard_unit: text,
        offers: z.array(offerSchema).catch([]).default([]),
      }),
    )
    .catch([])
    .default([]),
  /** Listed, never ranked: not enough attributes to say what they are. */
  not_comparable: z.array(offerSchema).catch([]).default([]),
});
export type SearchResult = z.infer<typeof searchResultSchema>;
export type ComparableGroup = SearchResult['comparable'][number];

/** `[{country, regions: [{region, areas[]}]}]` — an empty child list covers the whole parent. */
export const coverageSchema = z
  .array(
    z.object({
      country: z.string(),
      regions: z
        .array(z.object({ region: z.string(), areas: z.array(z.string()).catch([]).default([]) }))
        .catch([])
        .default([]),
    }),
  )
  .catch([])
  .default([]);
export type ServiceCoverage = z.infer<typeof coverageSchema>;

const profileFields = {
  organization_id: id,
  name: text,
  verification_status: z.string().default('unverified'),
  description: text,
  logo_url: text,
  website: text,
  categories: z.array(z.string()).catch([]).default([]),
  service_coverage: coverageSchema,
  delivers: z.boolean().catch(false).default(false),
  delivery_radius_km: z.number().nullable().catch(null).default(null),
  payment_methods: z.array(z.string()).catch([]).default([]),
  payment_terms: z.array(z.string()).catch([]).default([]),
  business_hours: text,
  return_terms: text,
};

/** The supplier's own profile (includes the private WhatsApp number). */
export const supplierProfileSchema = z.object({ ...profileFields, whatsapp_e164: text });
export type SupplierProfile = z.infer<typeof supplierProfileSchema>;

export const branchSchema = z.object({
  id,
  name: text,
  is_primary: z.boolean().catch(false).default(false),
  address: text,
  country: text,
  region: text,
  area: text,
  latitude: z.coerce.number().nullable().catch(null).default(null),
  longitude: z.coerce.number().nullable().catch(null).default(null),
  phone_e164: text,
  email: text,
  // Module 12: where it is, when it opens, where it delivers.
  town: text,
  opening_hours: text,
  delivery_counties: z.array(z.string()).catch([]).default([]),
  delivery_radius_km: z.number().nullable().catch(null).default(null),
  active: z.boolean().catch(true).default(true),
});
export type Branch = z.infer<typeof branchSchema>;

/** What buyers see: no contact numbers of people, no internal data. */
export const publicSupplierSchema = z.object({
  ...profileFields,
  branches: z.array(branchSchema).catch([]).default([]),
  offers: z.array(offerSchema).catch([]).default([]),
  /** Listed without a published price: buyers ask for a quotation (module 12). */
  on_request: z
    .array(
      z.object({
        supplier_product_id: id,
        original_name: text,
        brand: text,
        pack_description: text,
        stock_status: z.string().default('in_stock'),
        lead_time_days: integer,
      }),
    )
    .catch([])
    .default([]),
  rating: z
    .object({ average: z.number().nullable().catch(null).default(null), count: integer })
    .catch({ average: null, count: 0 })
    .default({ average: null, count: 0 }),
});
export type PublicSupplier = z.infer<typeof publicSupplierSchema>;

/** A supplier in the directory buyers invite to quote (public fields only). */
export const directorySupplierSchema = z.object({
  organization_id: id,
  name: text,
  verification_status: z.string().default('unverified'),
  logo_url: text,
  categories: z.array(z.string()).catch([]).default([]),
  service_coverage: coverageSchema,
  delivers: z.boolean().catch(false).default(false),
  product_count: z.number().int().catch(0).default(0),
});
export type DirectorySupplier = z.infer<typeof directorySupplierSchema>;

/** A company image (logo or product photo) and where it stands. */
export const orgAssetSchema = z.object({
  id,
  purpose: text,
  /** pending | uploaded | available | rejected */
  status: z.string().default('pending'),
  url: z.string().nullable().catch(null).default(null),
  thumb_url: z.string().nullable().catch(null).default(null),
});
export type OrgAsset = z.infer<typeof orgAssetSchema>;

export const supplierProductSchema = z.object({
  id,
  category_id: text,
  /** The generic product it was matched to (null: not enough attributes). */
  product_id: nullableId,
  canonical_name: z.string().nullable().catch(null).default(null),
  /** proposed (until a curator confirms it) | active */
  canonical_status: z.string().nullable().catch(null).default(null),
  original_name: text,
  original_description: text,
  brand: text,
  sku: text,
  attributes,
  pack_description: text,
  standard_units_per_pack: decimal,
  min_order_packs: decimal,
  stock_status: z.string().default('in_stock'),
  lead_time_days: integer,
  image_urls: z.array(z.string()).catch([]).default([]),
  active: z.boolean().catch(true).default(true),
  price: nullableMoney,
  price_updated_at: date,
  // Module 12
  tax_inclusive: z.boolean().catch(true).default(true),
  price_valid_until: date,
  tiers: z.array(priceTierSchema).catch([]).default([]),
  variant_group: nullableId,
  variant_label: text,
  vat_category: z.string().catch('standard').default('standard'),
  price_on_request: z.boolean().catch(false).default(false),
  version,
});
export type SupplierProduct = z.infer<typeof supplierProductSchema>;

export const priceRecordSchema = z.object({
  price: moneySchema,
  tax_inclusive: z.boolean().catch(true).default(true),
  effective_from: date,
  source: text,
  source_ref: text,
  min_quantity: decimal,
  valid_until: date,
});
export type PriceRecord = z.infer<typeof priceRecordSchema>;

/** The supplier's view of a purchase order addressed to it. */
export type SupplierOrder = PurchaseOrderDetail;
